/**
 * The Automations screen's data — every read and write goes through the TS SDK's
 * `Trigger` methods; nothing here builds a request of its own.
 *
 * Polled, not pushed: `trigger.*` is deliberately not forwarded to the app (the
 * pin test in tests/unit/test_trigger_tags.py says why and what would change it),
 * and the history lives on disk, not on the bus. Polling stops while the browser
 * tab is hidden (react-query's default `refetchIntervalInBackground: false`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounced } from '@src/hooks/useDebounced';
import {
  Agent,
  Trigger,
  type AutomationCheck,
  type AutomationOverview,
  type AutomationRun,
  type AutomationSample,
  type AutomationSummary,
  type BusMap,
  type CloudSchedule,
  type AutomationTestEvent,
  type ITrigger,
  type NextRuns,
  type RunOnceStarted,
  type RunsQuery,
  type TryRow,
} from '@sdk';
import type { DecisionVerdict } from '@sdk/models/ReturnedValue';

/** How often the list and the runs refresh while the screen is open. */
const AUTOMATIONS_POLL_MS = 5000;

const automationKeys = {
  all: ['automations'] as const,
  overview: () => ['automations', 'overview'] as const,
  startedLastHour: () => ['automations', 'started-last-hour'] as const,
  runs: (q: RunsQuery) =>
    ['automations', 'runs', q.triggerId ?? null, q.status ?? null, q.includeTests ?? true, q.includeBuiltin ?? true, q.limit ?? null] as const,
  run: (id: string | null) => ['automations', 'run', id] as const,
  nextRuns: (expr: string, kind: string, tz: string, n: number) => ['automations', 'next', expr, kind, tz, n] as const,
};

/** Every automation as a sentence with its health. `poll: false` for a screen that
 *  only needs the names (a filter list) — it shares the cache without adding a poll. */
/**
 * `data` is the rows; `cloudWarning` says why they are this machine's only — the overview's
 * `CLOUD_UNAVAILABLE` / `CLOUD_SIGNED_OUT` warning, or null when the cloud's were read too.
 */
export function useAutomations(options: { enabled?: boolean; poll?: boolean } = {}) {
  const query = useQuery<AutomationOverview, Error>({
    queryKey: automationKeys.overview(),
    queryFn: () => Trigger.overview(),
    refetchInterval: options.poll === false ? false : AUTOMATIONS_POLL_MS,
    enabled: options.enabled ?? true,
  });
  const overview = query.data;
  return {
    ...query,
    data: overview?.rows,
    cloudWarning: overview?.warnings.find((w) => w.error_code.startsWith('CLOUD_')) ?? null,
  };
}

/** One automation's summary, from the same list (no second request). */
export function useAutomation(triggerId: string | null | undefined, options: { poll?: boolean } = {}) {
  const query = useAutomations({ enabled: !!triggerId, poll: options.poll });
  const automation = triggerId ? ((query.data ?? []).find((a) => a.id === triggerId) ?? null) : null;
  return { ...query, automation };
}

export function useAutomationRuns(query: RunsQuery = {}, options: { enabled?: boolean } = {}) {
  return useQuery<AutomationRun[]>({
    queryKey: automationKeys.runs(query),
    queryFn: () => Trigger.runs(query),
    refetchInterval: AUTOMATIONS_POLL_MS,
    enabled: options.enabled ?? true,
  });
}

/** One run. Pass `enabled: false` when a polling list already holds it. */
export function useAutomationRun(runId: string | null | undefined, options: { enabled?: boolean } = {}) {
  return useQuery<AutomationRun>({
    queryKey: automationKeys.run(runId ?? null),
    queryFn: () => Trigger.run(runId as string),
    enabled: !!runId && (options.enabled ?? true),
    // A running run keeps changing; a finished one does not.
    refetchInterval: (q) =>
      q.state.data && ['running', 'launched'].includes(q.state.data.status) ? AUTOMATIONS_POLL_MS : false,
  });
}

/** When a schedule (saved or still being edited) fires next. Empty while the expression is blank. */
export function useNextRuns(
  schedule: { expr?: string | null; sched_trigger_type?: string | null; timezone?: string | null },
  n = 5,
) {
  const expr = useDebounced((schedule.expr ?? '').trim());
  const kind = schedule.sched_trigger_type || 'cron';
  const tz = schedule.timezone || '';
  return useQuery<NextRuns>({
    queryKey: automationKeys.nextRuns(expr, kind, tz, n),
    queryFn: () => Trigger.nextRuns({ expr, sched_trigger_type: kind, timezone: tz }, n),
    enabled: !!expr,
    retry: false,
    staleTime: 30_000,
  });
}

/** Refresh what a write changes: the list, the runs, and the automation itself —
 *  not the bus map, the rule code or the schedule previews. */
function useInvalidateAutomations() {
  const client = useQueryClient();
  return () =>
    Promise.all(
      [automationKeys.overview(), ['automations', 'runs'], ['automations', 'run'], ['automations', 'trigger']].map(
        (queryKey) => client.invalidateQueries({ queryKey }),
      ),
    );
}

/** *Run once now*. The run keeps going in the background; the lists pick it up.
 *  `messageId` runs a stream inbox rule on that message (its real envelope). */
export function useRunOnce() {
  const invalidate = useInvalidateAutomations();
  return useMutation<
    RunOnceStarted,
    Error,
    { triggerId: string; event?: AutomationTestEvent | null; messageId?: string; source?: AutomationSummary['source'] }
  >({
    mutationFn: ({ triggerId, event, messageId, source }) =>
      Trigger.runNow({ id: triggerId, source }, event, { messageId }),
    onSettled: () => void invalidate(),
  });
}

// ── Stream stream inbox automations (docs/snippets/stream-inbox-automations.md) ───────────────────

/** The fast test: the rule's gate asked about text typed in, or one message. Records nothing.
 *  A saved, unchanged rule is asked by id; the builder's fields (`spec`) otherwise, so the sentence
 *  being typed is what is tested. */
export function useDecideOn() {
  return useMutation<
    DecisionVerdict,
    Error,
    { triggerId?: string | null; spec?: Partial<ITrigger>; text?: string; messageId?: string }
  >({
    mutationFn: ({ triggerId, spec, text, messageId }) =>
      spec ? Trigger.decideSpec(spec, { text, messageId }) : Trigger.decideOn(triggerId as string, { text, messageId }),
  });
}

/** The try list: recent messages on the rule's sources, each with what the gate says of it.
 *  Asked on demand (`refetch`) — every row not already decided for real costs one decision. */
export function useDecideOnRecent(triggerId: string | null | undefined, limit = 20) {
  return useQuery<TryRow[]>({
    queryKey: ['automations', 'try', triggerId ?? null, limit],
    queryFn: () => Trigger.decideOnRecent(triggerId as string, { limit }),
    enabled: false,
    retry: false,
  });
}

/** The agents a rule here may run — what the picker offers. */
export function useRunnableAgents() {
  return useQuery<Agent[]>({
    queryKey: ['automations', 'runnable-agents'],
    queryFn: () => Agent.runnableHere(),
    staleTime: 30_000,
  });
}

/** How often the top bar's counter refreshes: one light request a minute, and on focus. */
const STARTED_POLL_MS = 60_000;

/** The person's own automations started in the last hour — the top-bar counter. One light call
 *  (a count, not the overview), once a minute and on focus. */
export function useStartedLastHour(): number {
  const { data } = useQuery<number>({
    queryKey: automationKeys.startedLastHour(),
    queryFn: () => Trigger.startedLastHour(),
    refetchInterval: STARTED_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: STARTED_POLL_MS / 2,
  });
  return data ?? 0;
}

export function useSetAutomationEnabled() {
  const invalidate = useInvalidateAutomations();
  return useMutation<Trigger, Error, { triggerId: string; enabled: boolean }>({
    mutationFn: ({ triggerId, enabled }) => Trigger.setEnabled(triggerId, enabled),
    onSettled: () => void invalidate(),
  });
}

/** Create (no id) or update (id) an automation from its fields. */
export function useSaveAutomation() {
  const invalidate = useInvalidateAutomations();
  return useMutation<
    Trigger,
    Error,
    { triggerId?: string | null; fields: Partial<ITrigger> & { name: string }; cloud?: CloudSchedule | null }
  >({
    // An id is routed by the SDK; a new row is made on Flowpad cloud only when `cloud` says so.
    mutationFn: ({ triggerId, fields, cloud }) =>
      triggerId
        ? Trigger.updateAutomation(triggerId, fields)
        : cloud
          ? Trigger.createOnCloud(cloud)
          : Trigger.createAutomation(fields),
    onSettled: () => void invalidate(),
  });
}

export function useDeleteAutomation() {
  const invalidate = useInvalidateAutomations();
  return useMutation<void, Error, string>({
    mutationFn: (triggerId) => Trigger.remove(triggerId),
    onSettled: () => void invalidate(),
  });
}

/** The full Trigger row behind an automation — what the builder edits. A Flowpad-cloud row's is read
 *  from the hub (`Trigger.load` routes by id). */
export function useAutomationTrigger(triggerId: string | null | undefined) {
  return useQuery<Trigger | null>({
    queryKey: ['automations', 'trigger', triggerId ?? null],
    queryFn: () => Trigger.load(triggerId as string),
    enabled: !!triggerId,
  });
}

/** *Check* — a saved automation (by id) or the builder's unsaved fields. Run on demand. */
export function useAutomationCheck() {
  return useMutation<
    AutomationCheck,
    Error,
    { triggerId?: string | null; spec?: Partial<ITrigger>; event?: AutomationTestEvent | null }
  >({
    mutationFn: ({ triggerId, spec, event }) =>
      triggerId ? Trigger.check(triggerId, event) : Trigger.checkSpec(spec ?? {}, event),
  });
}

/** Recent real events to test an event automation with. Saved: its own; unsaved: by pattern. */
export function useAutomationSamples(source: {
  triggerId?: string | null;
  pattern?: string | null;
  target?: string | null;
}) {
  const pattern = useDebounced((source.pattern ?? '').trim());
  return useQuery<AutomationSample[]>({
    queryKey: ['automations', 'samples', source.triggerId ?? null, pattern, source.target ?? null],
    queryFn: () =>
      source.triggerId ? Trigger.samples(source.triggerId) : Trigger.recentEvents(pattern, source.target),
    enabled: !!source.triggerId || !!pattern,
    refetchInterval: AUTOMATIONS_POLL_MS,
  });
}

/** The event bus as a map — event types, counts, listeners. Also the event picker's catalog.
 *  Not polled: the live stream arrives over the socket, and the counts refresh on focus. */
export function useBusMap(options: { enabled?: boolean } = {}) {
  return useQuery<BusMap>({
    queryKey: ['automations', 'bus-map'],
    queryFn: () => Trigger.busMap(),
    enabled: options.enabled ?? true,
    staleTime: 10_000,
  });
}

/** An agent rule's `trigger.py` — read, and saved back. */
export function useRuleCode(triggerId: string | null | undefined, enabled = true) {
  const client = useQueryClient();
  const query = useQuery<string>({
    queryKey: ['automations', 'code', triggerId ?? null],
    queryFn: () => Trigger.getCode(triggerId as string),
    enabled: !!triggerId && enabled,
    retry: false,
  });
  const save = useMutation<void, Error, string>({
    mutationFn: (content) => Trigger.setCode(triggerId as string, content),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['automations', 'code', triggerId ?? null] }),
  });
  return { ...query, save };
}

/** Find agent rule folders on disk and list them as automations. */
export function useDiscoverRules() {
  const invalidate = useInvalidateAutomations();
  return useMutation<Trigger[], Error, void>({
    mutationFn: () => Trigger.discover(),
    onSettled: () => void invalidate(),
  });
}
